import {inject, Injectable} from "@angular/core";
import {HttpClient, HttpParams} from "@angular/common/http";
import {Observable} from "rxjs";

import {Community} from "../../models/community.model";
import {PageModel} from "../../models/page.model";
import {ApiRoutesService} from '../api-routes.service';
import {Hardware} from '../../models/hardware.model';
import {Member} from '../../models/member.model';
import {Installation} from "../../models/installation.model";

@Injectable({
  providedIn: 'root'
})
export class CommunityService {

  readonly #httpClient = inject(HttpClient);
  readonly #apiRoutes = inject(ApiRoutesService);


  public findMyCommunities(page: number, pageSize: number, search: string): Observable<PageModel<Community>> {
    const params = new HttpParams()
      .set('page', page.toString())
      .set('pageSize', pageSize.toString())
      .set('search', search);

    return this.#httpClient.get<PageModel<Community>>(this.#apiRoutes.community(), {params});
  }

  public createCommunity(data: any, profilePicture: File | null): Observable<Community> {
    const formData = new FormData();
    formData.append('community', new Blob([JSON.stringify(data)], {type: 'application/json'}));

    if (profilePicture) {
      formData.append('file', profilePicture, profilePicture.name);
    }

    return this.#httpClient.post<Community>(this.#apiRoutes.community(), formData);
  }

  public updateCommunity(communityId: string, data: any, profilePicture: File | null): Observable<Community> {
    const formData = new FormData();
    formData.append('community', new Blob([JSON.stringify(data)], {type: 'application/json'}));

    if (profilePicture) {
      formData.append('file', profilePicture, profilePicture.name);
    }

    return this.#httpClient.put<Community>(`${this.#apiRoutes.community()}/${communityId}`, formData);
  }

  public getCommunityById(id: string): Observable<Community> {
    return this.#httpClient.get<Community>(this.#apiRoutes.community() + "/" + id);
  }

  public findCommunityHardware(communityId: string): Observable<Hardware> {
    return this.#httpClient.get<Hardware>(`${this.#apiRoutes.community()}/${communityId}/hardwares`);
  }

  public findCommunityInstallations(communityId: string): Observable<Installation[]> {
    return this.#httpClient.get<Installation[]>(`${this.#apiRoutes.community()}/${communityId}/installations`);
  }

  public updateCommunityInstallation(communityId: string, installationId: string, updateInstallation: Installation): Observable<Installation> {
    return this.#httpClient.patch<Installation>(`${this.#apiRoutes.community()}/${communityId}/installations/${installationId}`, updateInstallation);
  }

  public createMembers(members: Member[], communityId: string): Observable<Member[]> {
    return this.#httpClient.post<Member[]>(`${this.#apiRoutes.community()}/${communityId}/members`, members);
  }

  public updateCommunityMember(member: Member, communityId: string, profileId: string) {
    return this.#httpClient.patch<Member>(`${this.#apiRoutes.community()}/${communityId}/members/${profileId}`, member);
  }

  public sendCommunityInvitation(communityId: string, profileId: string) {
    return this.#httpClient.post<Member>(`${this.#apiRoutes.community()}/${communityId}/members/${profileId}/code`, {});
  }

  public removeMember(communityId: string, profileId: string) {
    return this.#httpClient.delete(`${this.#apiRoutes.community()}/${communityId}/members/${profileId}`);
  }
}
